<template>
  <div>
    <v-container>
      <v-row class="mt-5">
        <v-col cols="12">
          <main-card>
            <h1 class="pb-3">Lernstrategien entwickeln und gezielt optimieren</h1>
            <p>
              Erfolgreiches Lernen hängt nicht allein vom Lerninhalt ab, sondern massgeblich von den eingesetzten Lernstrategien. Ein bewusst
              gesteuertes, flexibles Lernverhalten sowie ein breites Repertoire bewährter Lern und Arbeitsstrategien fördern die Lernwirksamkeit,
              stärken die Motivation und führen zu nachhaltig besseren Ergebnissen.
            </p>

            <p>
              Der <strong>digitale Selbsttest Schule</strong> nach Christoph Metzger unterstützt Lernende dabei, ihr persönliches
              Lernstrategienprofil systematisch zu reflektieren. Die Auswertung steht als <strong>PDF-Download</strong> zur Verfügung und wird neu
              durch <strong>praxisnahe Anreicherungen von Danielle Rosenbaum</strong> ergänzt*. Diese weiterführenden Inhalte geben konkrete Impulse
              und Anleitungen zur gezielten Weiterentwicklung zentraler Lernkompetenzen.
            </p>

            <p>
              Die Testabsolventinnen und absolventen erhalten fundierte Unterstützung unter anderem zu den Themen:
            </p>

            <ul class="ms-7 mb-5">
              <li>Motivation und Lernhaltung</li>
              <li>Zeitmanagement und Arbeitsorganisation</li>
              <li>Konzentration und Informationsverarbeitung</li>
              <li>Umgang mit Stress und Prüfungsangst</li>
              <li>Selbstkontrolle und Prüfungsstrategien</li>
            </ul>
          </main-card>
        </v-col>

        <v-col cols="12" lg="6">
          <main-card>
            <h3>Anreicherungen und persönliche Empfehlungen für Lernstrategien</h3>

            <p> Der Zugriff auf die Ergebnisse sowie auf die darauf basierenden
              Empfehlungen und Anreicherungen bleibt <strong>dauerhaft</strong> bestehen und ermöglicht eine längerfristige Begleitung der
              individuellen Lernentwicklung.
            </p>

            <h3>Nachhaltiger Einsatz durch Wiederholungsmöglichkeit als Schullizenz bestellbar*</h3>

            <p>
              Mit dem Erwerb einer Lizenz stehen <strong>zwei Zugangscodes</strong> zur Verfügung, sodass der Selbsttest nach mehreren Monaten erneut
              durchgeführt werden kann. Jeder Code ist einmalig einlösbar.
            </p>
            <p>
              * Per E-Mail an <a href="mailto:verlag@cornelsen.ch"> verlag@cornelsen.ch
              </a>
            </p>

            <div class="collapse-container">
              <div class="collapse-header" @click="showDetails = !showDetails">
                <span>Aufbau und Inhalte des digitalen Selbsttests Schule</span>
                <span class="collapse-icon">
                  {{ showDetails ? "▲" : "▼" }}
                </span>
              </div>
              <div v-if="showDetails" class="collapse-content">
                <p>
                  Der digitale Selbsttest Schule basiert auf der <strong>zweiten Ausgabe des LASSI
                  (Learning and Study Strategies Inventory)</strong>. Die Lernenden bewerten <strong>72 Aussagen</strong>
                  zu ihrem eigenen Lernverhalten. Diese sind systematisch <strong>neun Kategorien</strong> zugeordnet:
                </p>

                <ul class="ms-7 mb-5">
                  <li>Haltung</li>
                  <li>Motivation</li>
                  <li>Umgang mit Zeit</li>
                  <li>Konzentration</li>
                  <li>Umgang mit Angst</li>
                  <li>Wesentliches erkennen</li>
                  <li>Informationen verarbeiten</li>
                  <li>Prüfungsstrategien</li>
                  <li>Selbstkontrolle</li>
                </ul>

                <p>
                  Die Auswertung erfolgt auf Basis einer <strong>Referenzgruppe</strong> und ermöglicht dadurch
                  eine fundierte, differenzierte Einordnung des individuellen Lernverhaltens.
                </p>

                <p>
                  Zu jeder Kategorie werden prägnante Empfehlungen zur Weiterentwicklung formuliert.
                  Der Praxistransfer wird durch gezielte Anreicherungen unterstützt, darunter:
                </p>

                <ul class="ms-7 mb-5">
                  <li>kompakte Arbeitsblätter</li>
                  <li>Checklisten und Strategielisten</li>
                  <li>Zeitanalysen, Portfolios und Tabellen</li>
                  <li>konkrete Tipps und Beispiele</li>
                </ul>

                <p>
                  Ergänzend stehen Kurzinfos, Mindmaps, Zeitpläne und weitere methodische Impulse
                  zur Verfügung. Die Inhalte beziehen sich punktuell auf Christoph Metzgers Fachbuch
                  <strong>„Wie lerne ich? WLI Schule“</strong> und sind konsequent auf die jeweilige Bildungsstufe
                  abgestimmt. Das zugrunde liegende <strong>WLI Konzept</strong> trägt dem Bedarf Rechnung, Lernstrategien im
                  Verlauf der Ausbildung mehrfach zu reflektieren und weiterzuentwickeln.
                </p>

                <h3>Unterstützung für die Unterrichtspraxis</h3>

                <p>
                  Für Lehrpersonen, die den digitalen Selbsttest Schule im Unterricht einsetzen,
                  steht eine <strong>kompakte Handreichung</strong> von Danielle Rosenbaum als <strong>PDF-Download</strong> zur
                  Verfügung. Diese begleitet praxisnah durch alle Phasen des Einsatzes:
                </p>

                <ol class="ms-7 mb-5">
                  <li>Vorbereitung</li>
                  <li>Durchführung</li>
                  <li>Auswertung</li>
                  <li>didaktische Einbettung der Ergebnisse in den Unterricht</li>
                </ol>

                <p>
                  Die Handreichung unterstützt Lehrpersonen dabei, Lernstrategien gezielt zu
                  thematisieren und nachhaltig im Unterricht zu verankern.
                </p>

                <p>
                  <strong>
                    Testzugänge sind im Cornelsen Online-Shop erhältlich.
                  </strong>
                </p>
              </div>
            </div>

            <Button
                  :large="true"
                  :button-text="'Information für Lehrpersonen'"
                  @click="openTeacherPdf"
              />
            <p></p>
            <p>
              * Die Anreicherungen sind differenziert auf zwei Bildungsstufen ausgerichtet:
              <strong>Sekundarstufe I</strong> sowie <strong>Sekundarstufe II</strong> mit Fokus auf <strong>KV, BM und Gymnasium.</strong>
              Der digitale Selbsttest <strong>Hochschule</strong> enthält keine Anreicherungen.
            </p>

            <div class="d-flex">
<!--              <Button-->
<!--                  class="mr-3"-->
<!--                  :to="'/anleitung/'"-->
<!--                  :large="true"-->
<!--                  :button-text="'Anleitung'"-->
<!--              />-->

            </div>
            <br>
            <p>
              Sie haben noch keinen Code? Dann besuchen Sie den <a
                  :href="'https://www.cornelsen.ch/'"
                  target="_blank"
                  class="text-primary"
              >Onlineshop</a> von Cornelsen Schweiz.
            </p>
          </main-card>
        </v-col>

        <v-col cols="12" lg="6">
          <v-card class="pa-10">
            <h3>Vorschau</h3>
            <v-divider class="mt-1 mb-5"/>

            <v-carousel cycle hide-delimiters>
              <v-carousel-item
                  v-for="(item, i) in sliderItems"
                  :key="i"
                  :src="item.src"
                  reverse-transition="fade-transition"
                  transition="fade-transition"
              />
            </v-carousel>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import MainCard from "@/components/layout/MainCard";
import Button from "@/components/layout/Button";

export default {
  name: "Index",
  components: {
    Button,
    MainCard
  },
  data: () => ({
    participant: null,
    showDetails: false,
    sliderItems: [
      {src: require("../assets/AdobeStock_352601877.jpeg")},
      {src: require("../assets/WLI_Anleitung-Nutzung_1.png")},
      {src: require("../assets/AdobeStock_559977564.jpeg")},
      {src: require("../assets/WLI_Auswertung-Legende.png")},
      {src: require("../assets/AdobeStock_1209763586.jpeg")},
      {src: require("../assets/WLI_Auswertung-ohne-Vorschlag.png")},
      {src: require("../assets/WLI_Fragebogen-Seite-1.png")},
    ],
  }),
  methods: {
    openTeacherPdf() {
      window.open(
          "/documents/WLI-Schule_digitaler Selbsttest_Handreichung.pdf",
          "_blank"
      );
    },
  },
};
</script>

<style>
.collapse-container {
  border: 1px solid #d9d9d9;
  margin-bottom: 20px;
}

.collapse-header {
  background-color: #f3f4f6;
  border-bottom: 1px solid #d9d9d9;
  padding: 12px 16px;
  cursor: pointer;

  display: flex;
  justify-content: space-between;
  align-items: center;

  font-weight: 600;
  font-size: 16px;
}

.collapse-header:hover {
  background-color: #ececec;
}

.collapse-icon {
  font-size: 14px;
}

.collapse-content {
  padding: 20px;
  background: #fff;
}
</style>